import React, { useMemo, useState, useEffect } from 'react';
import { FileText } from 'lucide-react';
import type { Invoice, Subscription } from '../../../../types/subscriptionManagement';
import { toInr, loadFxRates, getFxRates } from '../../../../utils/fxUtils';
import {
  allocatePaidInvoiceInrToMonths,
  hasPaidExpenseInMonths,
  isPaidExpenseInvoice,
} from '../../../../utils/subscriptionExpenseAllocation';
import {
  fyLabel,
  fyMonths,
  monthKeysFromInvoicesAllPeriods,
} from '../../../../utils/subscriptionFyUtils';
import { ExpenseChart, type ChartPoint } from './ExpenseChart';
import { ExpenseTable, type GroupRow } from './ExpenseTable';
import { roundInrForReportDisplay } from './formatInrReportMoney';

interface Props {
  subscriptions: Subscription[];
  /** Invoices scoped to the selected FY (parent filters). */
  invoices: Invoice[];
  /** Single Indian FY, or all periods for column/month span. */
  fyStart: number | 'all';
}

// ─── Data Aggregation ─────────────────────────────────────────────────────────

function buildExpenseData(
  invoices: Invoice[],
  subscriptions: Subscription[],
  months: string[],
  groupBy: 'department' | 'category',
) {
  const monthSet = new Set(months);
  const subMap = new Map(subscriptions.map(s => [s.id, s]));

  const paid = invoices.filter(isPaidExpenseInvoice);

  const grouped: Record<string, Record<string, number>> = {};
  const chartMap: Record<string, { total: number; count: number }> = {};
  months.forEach(ym => { chartMap[ym] = { total: 0, count: 0 }; });

  paid.forEach(inv => {
    const sub = subMap.get(inv.subscriptionId);
    const label = (groupBy === 'department' ? sub?.department : sub?.category) || 'Other';
    const inr = toInr(Number(inv.amount) || 0, inv.currency || 'INR', {
      invoiceDate: inv.invoiceDate,
    });
    const buckets = allocatePaidInvoiceInrToMonths(inv, inr);

    let countMonth: string | null = null;
    for (const [ym, portion] of Object.entries(buckets)) {
      if (!monthSet.has(ym)) continue;
      if (!countMonth) countMonth = ym;
      if (!grouped[label]) grouped[label] = {};
      grouped[label][ym] = (grouped[label][ym] || 0) + portion;
      chartMap[ym].total += portion;
    }
    if (countMonth) chartMap[countMonth].count += 1;
  });

  const rows: GroupRow[] = Object.entries(grouped)
    .map(([label, monthly]) => ({
      label,
      monthly,
      total: Object.values(monthly).reduce((s, v) => s + v, 0),
    }))
    .sort((a, b) => b.total - a.total);

  const columnTotals: Record<string, number> = {};
  months.forEach(ym => {
    columnTotals[ym] = rows.reduce((s, r) => s + (r.monthly[ym] || 0), 0);
  });
  const grandTotal = rows.reduce((s, r) => s + r.total, 0);

  const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const chartData: ChartPoint[] = months.map(ym => {
    const [y, m] = ym.split('-');
    return {
      label: `${MONTH_SHORT[Number(m) - 1]} '${y.slice(2)}`,
      total: roundInrForReportDisplay(chartMap[ym].total),
      count: chartMap[ym].count,
    };
  });

  return { rows, columnTotals, grandTotal, chartData };
}

// ─── Component ────────────────────────────────────────────────────────────────

export const MonthlyExpenseReport: React.FC<Props> = ({
  subscriptions,
  invoices,
  fyStart,
}) => {
  useEffect(() => { loadFxRates(); }, []);

  const [groupBy, setGroupBy] = useState<'department' | 'category'>('department');

  const months = useMemo(() => {
    if (fyStart === 'all') return monthKeysFromInvoicesAllPeriods(invoices);
    return fyMonths(fyStart);
  }, [fyStart, invoices]);

  const periodLabel = fyStart === 'all' ? 'All FY' : fyLabel(fyStart);

  const allowedMonths = useMemo(() => new Set(months), [months]);

  const { rows, columnTotals, grandTotal, chartData } = useMemo(
    () => buildExpenseData(invoices, subscriptions, months, groupBy),
    [invoices, subscriptions, months, groupBy],
  );

  const hasPaidInFy = useMemo(
    () => hasPaidExpenseInMonths(invoices, allowedMonths),
    [invoices, allowedMonths],
  );

  return (
    <div className="space-y-4">

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">

        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-1 h-4 rounded-full bg-blue-500 shrink-0" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
              Paid Expenses — INR — {periodLabel}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
            {(['department', 'category'] as const).map(g => (
              <button
                key={g}
                type="button"
                onClick={() => setGroupBy(g)}
                className={`px-3 py-1.5 rounded-md text-[11px] font-semibold transition-all capitalize ${
                  groupBy === g
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {g}
              </button>
            ))}
          </div>
          </div>
        </div>

        {!hasPaidInFy ? (
          <div className="py-20 text-center">
            <FileText size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm font-medium text-slate-500">No paid invoices yet</p>
            <p className="text-xs text-slate-400 mt-1">
              Mark invoices as Paid to see the expense report. Spend is prorated by coverage dates when set.
            </p>
          </div>
        ) : (
          <>
            <div className="px-5 pt-4 pb-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">
                Monthly Trend — {periodLabel}
              </p>
              <ExpenseChart data={chartData} />
            </div>

            <div className="border-t border-slate-100 mx-5" />

            <div className="px-5 py-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">
                Breakdown by {groupBy === 'department' ? 'Department' : 'Category'}
              </p>
              <ExpenseTable
                months={months}
                rows={rows}
                columnTotals={columnTotals}
                grandTotal={grandTotal}
              />
            </div>

            <div className="px-5 pb-3">
              <p className="text-[10px] text-slate-400">
                {(() => {
                  const r = getFxRates();
                  return `FX by invoice month (config/fx_rates.json); latest — USD: ₹${r.USD ?? '—'} · GBP: ₹${r.GBP ?? '—'} · EUR: ₹${r.EUR ?? '—'} · paid only · coverage-prorated`;
                })()}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
